import { CurrentInsightsEntity } from 'hooks/useCurrentInsightsEntity';
import {
  InsightsView,
  InsightsViewGadget,
} from 'contextProviders/InsightsContentProvider/viewTypes';

const MAX_STARTERS = 3;

// Whether a metric DISPLAY NAME is unambiguous enough to put in a chip. A chip
// is plain text, so the metric id we know here is lost; a name shared by several
// metrics ("Total Miles" across datasets) can only produce a clarifying question.
//
// This is necessary but NOT sufficient: the agent answers from the reports this
// account has flagged for it (list_domain_content → Skill), so a perfectly
// unambiguous name with no AI-ready report behind it still dead-ends. Only the
// server knows that roster, which is why every chip below is now anchored to
// something the user can actually see on screen rather than sampled from the
// metric catalogue.
type IsMetricAnswerable = (name: string) => boolean;
const ALWAYS_ANSWERABLE: IsMetricAnswerable = () => true;

// The first metric display name on a gadget that resolves cleanly, or undefined
// when the gadget carries no such metric (unnamed, or an ambiguous/special name).
const gadgetMetricName = ({
  gadget,
  isMetricAnswerable,
}: {
  gadget: InsightsViewGadget;
  isMetricAnswerable: IsMetricAnswerable;
}): string | undefined =>
  gadget.metrics
    .map((m) => m.name)
    .find((name): name is string => Boolean(name) && isMetricAnswerable(name!));

// A dimension whose axis is time (a trend chart's x-axis), detected from its
// humanised label/field. "Break down X by Date" just restates the trend already
// on screen — for these we phrase a trend question instead of a breakdown.
const DATE_DIMENSION_RE = /\b(date|day|week|month|quarter|year|time)\b/i;
const isDateDimension = (
  dimension: InsightsViewGadget['dimensionA'],
): boolean =>
  !!dimension &&
  (DATE_DIMENSION_RE.test(dimension.label) ||
    DATE_DIMENSION_RE.test(dimension.field));

// A gadget slices by a real category (region, driver manager, lane…) — the most
// insightful thing to offer a "break down" chip for, as opposed to a time trend.
const hasCategoricalBreakdown = (gadget: InsightsViewGadget): boolean =>
  !!gadget.dimensionA && !isDateDimension(gadget.dimensionA);

// One starter question derived from a single gadget, phrased around the real
// metric and — where present — the real categorical dimension or reproducible
// trend bucket that is actually on screen. A date axis is treated as a trend,
// never a breakdown; and we never propose an unreproducible trend (fiscal
// intervals, hour-of-day, etc.), which the tools can't answer.
const questionFromGadget = ({
  gadget,
  metricName,
}: {
  gadget: InsightsViewGadget;
  metricName: string;
}): string => {
  if (hasCategoricalBreakdown(gadget)) {
    return `Break down ${metricName} by ${gadget.dimensionA!.label}`;
  }
  if (gadget.trend && gadget.isTrendReproducible !== false) {
    return `How is ${metricName} trending by ${gadget.trend}?`;
  }
  return `How is ${metricName} trending this month?`;
};

// Derive starters from the live on-screen view: real metrics, real dimension
// labels, real trend buckets. Only uses metrics whose name resolves cleanly.
// Returns null when no gadget yields a usable metric chip, so the caller falls
// through to the metric-name templates.
const starterQuestionsFromView = ({
  view,
  entityName,
  isMetricAnswerable,
}: {
  view: InsightsView;
  entityName: string;
  isMetricAnswerable: IsMetricAnswerable;
}): string[] | null => {
  // Surface categorical breakdowns (a "by driver manager" table) ahead of trend
  // charts, whose over-time story a "trending by month" chip barely adds to —
  // both preserve reading order within their group.
  const ranked = [
    ...view.gadgets.filter(hasCategoricalBreakdown),
    ...view.gadgets.filter((gadget) => !hasCategoricalBreakdown(gadget)),
  ];

  const metricChips: string[] = [];
  const seenMetrics = new Set<string>();
  ranked.forEach((gadget) => {
    if (metricChips.length >= MAX_STARTERS) {
      return;
    }
    const metricName = gadgetMetricName({ gadget, isMetricAnswerable });
    if (!metricName || seenMetrics.has(metricName)) {
      return;
    }
    seenMetrics.add(metricName);
    metricChips.push(questionFromGadget({ gadget, metricName }));
  });

  if (!metricChips.length) {
    return null;
  }

  const questions = metricChips.slice(0, MAX_STARTERS - 1);
  if (view.comparison) {
    // Only assert a comparison the report is actually running; otherwise "vs last
    // month" is misleading for, e.g., a rolling-year scope with none selected.
    questions.push(`What changed in ${entityName} ${view.comparison}?`);
  } else if (metricChips[MAX_STARTERS - 1]) {
    questions.push(metricChips[MAX_STARTERS - 1]);
  } else {
    questions.push(`Summarise ${entityName} for the current period`);
  }

  return questions.slice(0, MAX_STARTERS);
};

// Starters built from the entity's own metric names, for a report/dashboard
// that publishes no view. The compare chip is only used when TWO clean names
// exist, so a chip never pairs a name that would send the agent into a
// clarifying question.
const starterQuestionsFromMetricNames = ({
  names,
  entityName,
}: {
  names: string[];
  entityName?: string;
}): string[] => {
  const [first, second] = names;
  const trailer = entityName
    ? `What changed in ${entityName} vs last month?`
    : 'What changed vs last month?';
  return [
    `How is ${first} trending this month?`,
    second
      ? `Compare ${first} and ${second} this quarter`
      : `What stands out about ${first}?`,
    trailer,
  ];
};

// Starter questions for a new chat session, most-tailored first:
//   1. the live on-screen view (real metrics + dimensions + trend)
//   2. the entity's real metric names (that resolve cleanly)
//   3. the entity name alone
//   4. the scoped workspace name — names no metric, so the agent routes it
//      itself against whatever content it actually has
//   5. nothing
//
// There is deliberately no generic fallback. A chip naming a metric is a promise
// the agent can answer about it, and away from the user's screen we cannot make
// that promise: the agent answers only from reports this account flagged for it,
// which lives server-side. An invented chip that dead-ends is worse than an
// empty space — it is usually the first thing a new user clicks.
const getStarterQuestions = ({
  entity,
  workspaceName,
  isMetricAnswerable = ALWAYS_ANSWERABLE,
}: {
  entity: CurrentInsightsEntity | null;
  workspaceName?: string;
  isMetricAnswerable?: IsMetricAnswerable;
}): string[] => {
  if (!entity) {
    if (workspaceName) {
      return [
        `What are the key metrics in ${workspaceName}?`,
        `Summarise ${workspaceName} performance this month`,
        `What changed in ${workspaceName} vs last month?`,
      ];
    }
    return [];
  }

  if (entity.view) {
    const fromView = starterQuestionsFromView({
      view: entity.view,
      entityName: entity.entityName,
      isMetricAnswerable,
    });
    if (fromView) {
      return fromView;
    }
  }

  const answerableNames = entity.metricNames.filter(isMetricAnswerable);

  if (!answerableNames.length) {
    return [
      `Summarise ${entity.entityName} for this month`,
      `What changed in ${entity.entityName} vs last month?`,
      `What stands out in ${entity.entityName}?`,
    ];
  }

  return starterQuestionsFromMetricNames({
    names: answerableNames,
    entityName: entity.entityName,
  });
};

export default getStarterQuestions;
